import React, { useState, useEffect, useMemo } from 'react';

import usdtLogo from '@/assets/logos/usdt.png';

import classNames from 'classnames';
import { RaceData, Token } from '@/pages/race-wizard';
import { getTokenColor, formatUSDC } from '@/utils/tokens';
import TokensMetricsChart from './tokens-metrics-chart';

const VictoryMetricsAndEdgeView = ({
  onNext,
  onBack,
  tokens,
}: {
  onNext: (data: Partial<RaceData>) => void;
  onBack: () => void;
  tokens: Token[];
}) => {
  const [usdcAmount, setUsdcAmount] = useState<string>('');
  const [allocations, setAllocations] = useState<Record<string, number>>({});
  const [totalAllocation, setTotalAllocation] = useState<number>(0);
  const [inputError, setInputError] = useState<string>('');
  const [creatorCommission, setCreatorCommission] = useState<number>(0);
  const [pieData, setPieData] = useState<Array<{ name: string; value: number; color: string }>>([]);
  const [showHandicapping, setShowHandicapping] = useState<boolean>(false);

  const calculateUSDCValue = (percentage: number) => {
    if (!usdcAmount || percentage === 0) return 0;
    return (parseFloat(usdcAmount) * percentage) / 100;
  };

  const handleUSDCInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    if (value === '') {
      setUsdcAmount('');
      setInputError('');
      return;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      setInputError('Please enter a valid number');
    } else if (numValue <= 0) {
      setInputError('Amount must be greater than 0');
    } else {
      setInputError('');
    }
    setUsdcAmount(value);
  };

  const handleAllocationChange = (symbol: string, newValue: number) => {
    // Ensure newValue is within valid range
    const clampedValue = Math.max(0, Math.min(100, newValue));

    // Get current allocations and the token being changed
    const currentAllocations = { ...allocations };
    const oldValue = currentAllocations[symbol] || 0;
    const delta = clampedValue - oldValue;

    // If no change, return early
    if (delta === 0) return;

    // Calculate total allocation of other tokens
    const otherTokens = Object.keys(currentAllocations).filter(key => key !== symbol);
    const otherTokensTotal = otherTokens.reduce((sum, key) => sum + currentAllocations[key], 0);

    // Set the new value for the changed token
    currentAllocations[symbol] = clampedValue;

    // Special case: If we're reducing a token with 100% allocation
    if (oldValue === 100 && delta < 0) {
      // Distribute the reduction equally among other tokens
      const equalShare = Math.floor((100 - clampedValue) / otherTokens.length);
      const remainder = 100 - clampedValue - equalShare * otherTokens.length;

      otherTokens.forEach((key, index) => {
        // Add the remainder to the first token to ensure total is exactly 100
        currentAllocations[key] = index === 0 ? equalShare + remainder : equalShare;
      });

      const total = Object.values(currentAllocations).reduce((sum, value) => sum + value, 0);
      const data = tokens
        .filter(token => currentAllocations[token.symbol] > 0)
        .map(token => ({
          name: token.symbol,
          value: currentAllocations[token.symbol],
          color: getTokenColor(token.symbol),
        }));
      setAllocations(currentAllocations);
      setTotalAllocation(total);
      setPieData(data);
      return;
    }

    // If other tokens have allocations, adjust them proportionally
    if (otherTokensTotal > 0) {
      // Calculate how much to adjust other tokens
      const targetOtherTotal = 100 - clampedValue;

      // If other tokens need adjustment
      if (otherTokensTotal !== targetOtherTotal) {
        // Calculate scaling factor
        const scaleFactor = targetOtherTotal / otherTokensTotal;

        // Apply scaling to other tokens
        otherTokens.forEach(key => {
          currentAllocations[key] = Math.max(0, Math.round(currentAllocations[key] * scaleFactor));
        });

        // Handle rounding errors to ensure total is exactly 100
        const newTotal = Object.values(currentAllocations).reduce((sum, value) => sum + value, 0);
        if (newTotal !== 100) {
          // Find the token with the largest allocation (excluding the one being changed)
          // and adjust it to make the total exactly 100
          const largestOtherToken = otherTokens.reduce(
            (largest, key) => (currentAllocations[key] > currentAllocations[largest] ? key : largest),
            otherTokens[0]
          );

          currentAllocations[largestOtherToken] += 100 - newTotal;
        }
      }
    } else if (delta < 0) {
      // If we're reducing the allocation but other tokens are at 0
      // Distribute the reduction equally among other tokens
      const equalShare = Math.floor((100 - clampedValue) / otherTokens.length);
      const remainder = 100 - clampedValue - equalShare * otherTokens.length;

      otherTokens.forEach((key, index) => {
        // Add the remainder to the first token to ensure total is exactly 100
        currentAllocations[key] = index === 0 ? equalShare + remainder : equalShare;
      });
    } else {
      // If there are no other tokens with allocations, set this token to 100%
      currentAllocations[symbol] = 100;
    }

    const total = Object.values(currentAllocations).reduce((sum, value) => sum + value, 0);
    const data = tokens
      .filter(token => currentAllocations[token.symbol] > 0)
      .map(token => ({
        name: token.symbol,
        value: currentAllocations[token.symbol],
        color: getTokenColor(token.symbol),
      }));
    setAllocations(currentAllocations);
    setTotalAllocation(total);
    setPieData(data);
  };

  const isValid = useMemo(
    () => totalAllocation === 100 && usdcAmount && !inputError,
    [totalAllocation, usdcAmount, inputError]
  );

  const validatorRewardPosition = useMemo(() => {
    const validatorReward = Math.max(1, creatorCommission * 0.2);
    return `${validatorReward.toFixed(1)}%`;
  }, [creatorCommission]);

  // Initialize allocations with equal distribution
  useEffect(() => {
    if (tokens?.length) {
      const equalShare = Math.floor(100 / tokens.length);
      const remainder = 100 - equalShare * tokens.length;

      const initialAllocations: Record<string, number> = {};
      tokens.forEach((token, index) => {
        // Add the remainder to the first token to ensure total is exactly 100
        initialAllocations[token.symbol] = index === 0 ? equalShare + remainder : equalShare;
      });

      const total = Object.values(initialAllocations).reduce((sum, value) => sum + value, 0);
      const data = tokens
        .filter(token => initialAllocations[token.symbol] > 0)
        .map(token => ({
          name: token.symbol,
          value: initialAllocations[token.symbol],
          color: getTokenColor(token.symbol),
        }));
      setAllocations(initialAllocations);
      setTotalAllocation(total);
      setPieData(data);
    }
  }, [tokens]);

  return (
    <React.Fragment>
      <div>
        <h3 className="text-xl font-bold">Handicapping</h3>
        <div className="mb-3 mt-1 leading-none">
          {showHandicapping ? (
            <>
              <span className="text-xs opacity-50">{`Highest { Percentage Return / 30 day Volatility } x`}</span> <br />
              <span className="text-xs opacity-50">{`Log { ( Token FDV / Min FDV among Tokens ) + 1 }`}</span>
            </>
          ) : (
            <>
              <span className="text-xs opacity-50">{`Percentage Return : { Price ( Race End ) - Price ( Race Start ) } / Price ( Race Start )`}</span>
            </>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            className={classNames(
              'w-20 cursor-pointer rounded-full border border-[#282133] bg-[#090409] px-3 py-1.5 text-xs font-medium transition-all duration-200 hover:opacity-80 sm:text-sm',
              {
                'bg-[#F2F2F7] text-[#31111D]': showHandicapping,
              }
            )}
            onClick={() => setShowHandicapping(true)}
          >
            Yes
          </button>
          <button
            className={classNames(
              'w-20 cursor-pointer rounded-full border border-[#282133] bg-[#090409] px-3 py-1.5 text-xs font-medium transition-all duration-200 hover:opacity-80 sm:text-sm',
              {
                'bg-[#F2F2F7] text-[#31111D]': !showHandicapping,
              }
            )}
            onClick={() => setShowHandicapping(false)}
          >
            No
          </button>
        </div>
      </div>

      <div className="h-[1px] w-full bg-white/40" />

      <h3 className="text-lg font-bold">Initialize Staking Multipliers</h3>

      <label className="flex flex-col gap-2">
        <span className="text-sm font-bold">Deposit</span>
        <div className="relative flex w-[145px] items-center justify-between gap-1 rounded-lg border border-[#282133] bg-purple-2 px-2 py-[6px]">
          <input type="number" value={usdcAmount} onChange={handleUSDCInput} className="" placeholder="0" />

          <img src={usdtLogo} alt="USDT" className="pointer-events-none absolute right-2 h-4 w-4 select-none" />
        </div>
        <div className="flex items-center gap-2 text-xs text-[#9D9DAA]">
          <span>Wallet balance</span>

          <span className="flex items-center gap-1">
            {/* TODO: Add actual wallet balance */}
            <strong className="text-white">0.00</strong>
            <img src={usdtLogo} alt="USDT" className="pointer-events-none size-3 select-none" />
            USDT
          </span>
        </div>
      </label>

      <div className="space-y-4">
        <div className="text-sm font-bold">Allocate</div>
        <div className="flex flex-col justify-between gap-6 lg:flex-row lg:gap-0">
          <div className="grow space-y-6">
            {tokens?.map(token => {
              const tokenAllocation = allocations[token.symbol] || 0;
              const usdcValue = calculateUSDCValue(tokenAllocation);

              return (
                <div key={token.symbol} className="flex gap-8">
                  <div className="flex w-fit flex-col items-center justify-between gap-2 text-sm">
                    <img src={token.image} alt={`${token.id}`} className="size-10 rounded-full object-contain" />
                    <span>{token.symbol}</span>
                  </div>

                  <div>
                    <div className="-mb-1 font-bold">{tokenAllocation}%</div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={tokenAllocation}
                      onChange={e => handleAllocationChange(token.symbol, parseInt(e.target.value))}
                      style={
                        {
                          '--value': `${tokenAllocation}%`,
                          '--track-height': '4px',
                        } as React.CSSProperties
                      }
                    />

                    <div className="mt-1 flex items-center gap-1 text-sm">
                      ${formatUSDC(usdcValue)}
                      <img src={usdtLogo} alt="USDT" className="size-4 rounded-full" />
                      <span>USDT</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <TokensMetricsChart pieData={pieData} tokens={tokens} allocations={allocations} />
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-bold">Race Creator Edge</h3>

        <div className="space-y-2">
          <div className="relative max-w-[200px]">
            <input
              type="range"
              min="0"
              max="10"
              className="rotate-180"
              value={creatorCommission}
              onChange={e => setCreatorCommission(parseInt(e.target.value))}
              style={{ '--value': `${creatorCommission * 10}%`, '--thumb-size': '10px' } as React.CSSProperties}
            />
            <div className="pointer-events-none flex select-none items-center justify-between text-xs">
              <span className={classNames({ 'opacity-0': creatorCommission === 9 })}>10%</span>
              <span className={classNames({ 'opacity-0': creatorCommission === 6 })}>5%</span>
              <span className={classNames({ 'opacity-0': creatorCommission === 1 })}>0%</span>
              <span
                className={classNames(`absolute left-[var(--range)]`, {
                  'opacity-0': creatorCommission === 0 || creatorCommission === 10 || creatorCommission === 5,
                })}
                style={
                  {
                    '--range': `${100 - creatorCommission * 10}%`,
                  } as React.CSSProperties
                }
              >
                {creatorCommission}%
              </span>
            </div>

            <div
              className="pointer-events-none my-2 ml-auto flex h-[1px] select-none justify-between bg-[#9C9C9C] text-xs transition before:relative before:-top-1 before:h-1 before:w-[1px] before:bg-current after:relative after:-top-1 after:h-1 after:w-[1px] after:bg-current"
              style={{ width: `${creatorCommission * 10}%` }}
            />

            <div className="-mr-8 -mt-1 text-end text-xs">Validator Reward ({validatorRewardPosition})</div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4">
        <button onClick={onBack} className="min-w-[40%] rounded-lg border border-[#9937F7] p-2 font-bold lg:min-w-40">
          Back
        </button>
        <button
          disabled={!isValid}
          onClick={() => {
            if (isValid) {
              onNext({
                allocations,
                usdcAmount: parseFloat(usdcAmount),
                creatorCommission,
                handicapSettings: { enabled: showHandicapping },
              });
            }
          }}
          className="min-w-[40%] rounded-lg bg-[#9937F7] p-2 font-bold lg:min-w-40"
        >
          Review
        </button>
      </div>
    </React.Fragment>
  );
};

export default VictoryMetricsAndEdgeView;
